import { Button, Modal } from "react-bootstrap";
import { useLingui } from "@lingui/react/macro";

import type { IPlantData } from "@/lib/backend/manufacturing2.generated.types";
import type { ModelsStagingFilterState } from "./modelsStaging.lib";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";

import ModelsStagingFilterFields from "./ModelsStagingFilterFields";

type Props = {
    show: boolean;
    plants: IPlantData[];
    exportFilters: ModelsStagingFilterState;
    exportAll: boolean;
    loading: boolean;
    error: string;
    onExportFiltersChange: (filters: ModelsStagingFilterState) => void;
    onExportAllChange: (exportAll: boolean) => void;
    onUseCurrentFilters: () => void;
    onExport: () => void;
    onClose: () => void;
};

const ModelsStagingExportModal = ({
    show,
    plants,
    exportFilters,
    exportAll,
    loading,
    error,
    onExportFiltersChange,
    onExportAllChange,
    onUseCurrentFilters,
    onExport,
    onClose,
}: Props) => {
    const { t } = useLingui();

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    return (
        <Modal show={show} dialogClassName="modal-dialog-scrollable" onHide={handleClose}>
            <Modal.Header>
                <Modal.Title>{t({ id: "common.export_csv", message: "Export CSV" })}</Modal.Title>
                <button type="button" className="close" onClick={handleClose} disabled={loading}>
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">{t`Close`}</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted mb-3">
                    {t`Configure filters below, or export all models for the organization. Matching rows from the entire result set will be exported, not just the current page.`}
                </p>

                <ErrorComponent msg={error} type="error" />

                <div className="form-check mb-3">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="models-staging-export-all"
                        checked={exportAll}
                        disabled={loading}
                        onChange={event => onExportAllChange(event.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="models-staging-export-all">
                        {t`Export all models (entire organization, no filters)`}
                    </label>
                </div>

                {!exportAll && (
                    <>
                        <div className="d-flex justify-content-end mb-2">
                            <Button variant="link" className="p-0" disabled={loading} onClick={onUseCurrentFilters}>
                                {t`Use current table filters`}
                            </Button>
                        </div>
                        <ModelsStagingFilterFields
                            plants={plants}
                            filters={exportFilters}
                            disabled={loading}
                            onFiltersChange={onExportFiltersChange}
                        />
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" disabled={loading} onClick={handleClose}>
                    {t`Cancel`}
                </Button>
                <Button variant="primary" className="btn_export" disabled={loading} onClick={onExport}>
                    {loading ? t`Exporting...` : t({ id: "common.export_csv", message: "Export CSV" })}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModelsStagingExportModal;
